class ProductSelector extends HTMLElement {
  constructor() {
    super();
    this.inputWrapper = '[data-input-wrapper]';
    this.variants = JSON.parse(
      this.querySelector('[type="application/json"]').textContent
    );
    this.form = document.querySelector(
      `#ProductForm-${this.dataset.sectionId}`
    );
    
    // DOM element selectors
    this.gallerySelector = '#product-media-gallery';
    this.mainSlidesSelector = '#product-media-gallery .swiper-slide:not(.swiper-thumbs .swiper-slide)';
    this.thumbSlidesSelector = '.swiper-thumbs .swiper-slide';
    
    // Save the initial state - which slides were hidden from the beginning
    this.saveInitialState();
    
    // Initialize Swiper directly on first load
    this.initSwiper();
    
    // Bind events
    this.addEventListener('change', this.onVariantChange.bind(this));
    window.addEventListener('scroll', this.debounce(this.reattachThumbnailHandlers.bind(this), 100));
    
    // Initialize
    this.updateOptions();
    this.updateVariant();
    this.initUpdateMedia();
  }
  
  saveInitialState() {
    this.initialHiddenSlides = new Set();
    
    // Record which slides were initially hidden
    document.querySelectorAll(this.mainSlidesSelector).forEach(slide => {
      if (slide.style.display === 'none' || slide.hasAttribute('data-hidden')) {
        const mediaId = slide.getAttribute('data-media-id');
        this.initialHiddenSlides.add(mediaId);
      }
    });
    
    document.querySelectorAll(this.thumbSlidesSelector).forEach(thumb => {
      if (thumb.style.display === 'none' || thumb.hasAttribute('data-hidden')) {
        const mediaId = thumb.getAttribute('data-media-id');
        this.initialHiddenSlides.add(mediaId);
      }
    });
    
    console.log("Initially hidden slides:", Array.from(this.initialHiddenSlides));
  }
  
  initSwiper() {
    const galleryElement = document.querySelector(this.gallerySelector);
    if (!galleryElement) return;

    // Get swiper options from data attribute if available
    let options = {};
    try {
      if (galleryElement.dataset.swiperOptions) {
        options = JSON.parse(galleryElement.dataset.swiperOptions);
      }
    } catch (e) {
      console.error('Error parsing Swiper options:', e);
    }
    
    // Store Swiper settings
    this.settings = {
      sliderElement: galleryElement,
      options: {
        slidesPerView: 1,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        pagination: {
          el: '.swiper-pagination',
          type: 'progressbar',
        },
        thumbs: {
          el: '.swiper-thumbs',
          slidesPerView: options.slidesPerViewDesktop || 4,
          direction: options.thumbsDirectionDesktop || 'vertical',
        },
        ...options
      },
      sliderInstance: null
    };
    
    // Ensure we have Swiper loaded
    if (typeof Swiper === 'undefined') {
      console.error('Swiper is not defined. Make sure it is loaded properly.');
      return;
    }
    
    // First, ensure all initially hidden slides are actually hidden
    this.enforceInitialHiddenState();
    
    // Create Swiper instance
    if (this.settings.sliderInstance) {
      this.settings.sliderInstance.destroy(true, true);
    }
    
    try {
      this.settings.sliderInstance = new Swiper(this.settings.sliderElement, this.settings.options);
      this.reattachThumbnailHandlers();
    } catch (e) {
      console.error('Error initializing Swiper:', e);
    }
  }
  
  // Make sure hidden slides stay hidden
  enforceInitialHiddenState() {
    document.querySelectorAll(this.mainSlidesSelector).forEach(slide => {
      const mediaId = slide.getAttribute('data-media-id');
      if (this.initialHiddenSlides.has(mediaId)) {
        slide.style.display = 'none';
        slide.setAttribute('aria-hidden', 'true');
      }
    });
    
    document.querySelectorAll(this.thumbSlidesSelector).forEach(thumb => {
      const mediaId = thumb.getAttribute('data-media-id');
      if (this.initialHiddenSlides.has(mediaId)) {
        thumb.style.display = 'none';
        thumb.setAttribute('aria-hidden', 'true');
      }
    });
  }
  
  reattachThumbnailHandlers() {
    const thumbs = document.querySelectorAll(this.thumbSlidesSelector);
    
    thumbs.forEach((thumb) => {
      const mediaId = thumb.getAttribute('data-media-id');
      
      // Skip hidden thumbnails
      if (thumb.style.display === 'none' || this.initialHiddenSlides.has(mediaId)) {
        return;
      }
      
      // Remove any existing handlers to prevent duplicates
      const clone = thumb.cloneNode(true);
      thumb.parentNode.replaceChild(clone, thumb);
      
      // Find corresponding main slide
      const mainSlides = Array.from(document.querySelectorAll(this.mainSlidesSelector))
        .filter(slide => slide.style.display !== 'none');
      
      const corresponding = mainSlides.find(slide => 
        slide.getAttribute('data-media-id') === mediaId
      );
      
      if (corresponding) {
        // Find index in visible slides array
        const visibleIndex = mainSlides.indexOf(corresponding);
        
        // Attach new handler
        clone.addEventListener('click', () => {
          if (this.settings.sliderInstance && visibleIndex !== -1) {
            this.settings.sliderInstance.slideTo(visibleIndex);
            
            // Update active states
            mainSlides.forEach((slide, i) => {
              if (i === visibleIndex) {
                slide.setAttribute('data-selected', '');
              } else {
                slide.removeAttribute('data-selected');
              }
            });
          }
        });
      }
    });
  }
  
  updateVariant() {
    this.currentVariant = this.getVariantData();
    if (!this.currentVariant) {
      console.warn('No variant found for the current selection');
      return;
    }
    
    console.log("Current Variant:", this.currentVariant);
    this.updateProductMedia();
  }

  onVariantChange(event) {
    if (event.target.type === 'number') return;
    this.updateOptions();
    this.updateVariant();
  }

updateProductMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;

    const featuredMediaId = this.currentVariant.featured_media.id.toString();
    const variantAltText = document.querySelector(`[data-media-id="${featuredMediaId}"]`)?.getAttribute('data-alt-text');

    let targetIndex = 0;
    let visibleSlides = [];
    
    // Filter main slides
    const allSlides = Array.from(document.querySelectorAll(this.mainSlidesSelector));
    allSlides.forEach((slide) => {
        const mediaId = slide.getAttribute('data-media-id');
        const slideAltText = slide.getAttribute('data-alt-text');

        const matchesVariant = !variantAltText || slideAltText === variantAltText;
        slide.style.display = matchesVariant ? 'block' : 'none';

        if (matchesVariant) {
            visibleSlides.push(slide);
            if (mediaId === featuredMediaId) {
                targetIndex = visibleSlides.length - 1; // Ensure featured image is focused
            }
        }
    });

    // Filter thumbnails
    const thumbSlides = Array.from(document.querySelectorAll(this.thumbSlidesSelector));
    thumbSlides.forEach((thumb) => {
        const mediaId = thumb.getAttribute('data-media-id');
        const slideAltText = thumb.getAttribute('data-alt-text');

        const matchesVariant = !variantAltText || slideAltText === variantAltText;
        thumb.style.display = matchesVariant ? 'block' : 'none';
    });

    // Force layout recalculation & update Swiper
    document.body.offsetHeight;
    
    setTimeout(() => {
        if (this.settings.sliderInstance) {
            this.settings.sliderInstance.update();
            this.settings.sliderInstance.slideTo(targetIndex, 300);
        }
    }, 100);
}


  getVariantData() {
    if (this.options.length === 0) {
      return this.variants[0];
    }

    const foundVariant = this.variants.find(variant => {
      return this.options.every(option => {
        const optionIndex = this.options.findIndex(opt => opt.name === option.name) + 1;
        return variant[`option${optionIndex}`] === option.value;
      });
    });

    return foundVariant;
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll('input[type="radio"]:checked, select'),
      el => ({ name: el.dataset.name || el.name, value: el.value })
    );
  }

  initUpdateMedia() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = parseInt(urlParams.get('variant'));

    if (variantIdFromUrl) {
      this.currentVariant = this.variants.find(variant => variant.id === variantIdFromUrl);
      if (this.currentVariant) {
        this.updateProductMedia();
      }
    }
  }
  
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  disconnectedCallback() {
    window.removeEventListener('scroll', this.debounce(this.reattachThumbnailHandlers.bind(this), 100));
    if (this.settings && this.settings.sliderInstance) {
      this.settings.sliderInstance.destroy();
    }
  }
}

customElements.define('product-selector', ProductSelector);